/**
 * ZipWidget - Leadgen Ad Block
 *
 * This class controls the DOM logic for the leadgen zip widget ad block.
 * This widget may or may not appear on leadgen articles.
 * All settings are defined in Block Editor and the HTML is rendered
 * via block Injector.
 *
 * It is possbile for there to be more than one Zip Widget on
 * a given page or post, and this can appear on any post type or template.
 *
 */
import validators from '../_validators';

/**
 * Prefill the zip widget input with a zip code
 *
 * @param userZip
 * @return void
 */
const fillUserZip = (userZip) => {
  const zipInput = document.querySelectorAll('.leadgen-zipwidget__field--zipcode');
  if (zipInput.length > 0) {
    zipInput.forEach((elem) => {
      const el = elem;
      el.value = userZip;
    });
  }
};

/**
 * Get the user's geolocated ZIP and pass it to the DOM function
 * Value comes from CloudFront via Block Injector
 *
 * @return void
 *
 */
const setUserZip = () => {
  // window.s1.blockInjector is set by Unified Block Injector
  const userData = window?.s1?.markupModifier || window?.s1?.blockInjector || {};

  const userZip = userData?.postalCode || null;

  if (userZip && Number.isInteger(parseInt(userZip, 10))) {
    fillUserZip(userZip);
  } else {
    console.warn('Detected Zip code is not a valid US Zip code (numeric)');
  }
};

/**
 * Check if the zip code value should be passed
 * into the click-wrapped offer URL to search or not
 *
 * @param zipWidgetEl - HTML instance of the zip widget
 * @return {boolean}
 */
const shouldPassZipCodeToPartner = (zipWidgetEl) => {
  let shouldPassZip = false;

  const passZip = zipWidgetEl.querySelector('.leadgen-zipwidget-partner-pass');

  if (passZip) {
    // BI will add a string of 'true' or 'false' into this hidden HTML input
    shouldPassZip = !!((passZip.value === 'true' || passZip.value === true));
  }

  return shouldPassZip;
};

/**
 * Construct the final destination URL
 * Append the zip code to the click-wrapped URL
 * This will pass it to search on between the final redirect
 * and make it available on the partner URL
 *
 * @param zipValue
 * @return {string}
 */
const formatFinalZipWidgetDestinationUrl = (zipWidgetEl, zipValue) => {
  let finalUrl = '';

  if (typeof zipWidgetEl !== 'undefined' && zipWidgetEl !== null) {
    // used to get the search params if they exist in the post meta URL value.
    const url = new URL(zipWidgetEl.action);
    const { searchParams } = url; // preserve existing search params

    // append the zip code to the search click-wrapped URL (form action)
    if (shouldPassZipCodeToPartner(zipWidgetEl)) {
      searchParams.set('zipcode', zipValue);
    }

    // build the final URL
    finalUrl = url.toString();
    console.log(`final zip widget url: ${finalUrl}`);
  }

  return finalUrl;
};

/**
 * Zipwidget GTM event
 * @param eventValue
 * @return void
 */
const zipWidgetGtmEvent = (eventValue) => {
  const dataLayer = window.dataLayer || [];
  dataLayer.push({
    event: 'zipwidget form submit',
    value: eventValue,
  });
};

/**
 * Zip Widget Form Submit Handler
 *
 * @param zipWidgetEl
 * @return void
 */
const onZipWidgetSubmit = (zipWidgetEl) => {
  zipWidgetEl.addEventListener('submit', (e) => {
    e.preventDefault(); // stop normal form submit.
    const data = new FormData(zipWidgetEl);
    const zipcode = data.get('zipcode');

    if (zipcode && validators.isZipValid(zipcode)) {
      // success.
      zipWidgetEl.classList.remove('leadgen-zipwidget--error');
      // send GTM event
      zipWidgetGtmEvent('zip submitted');
      // create a link to "click" instead of submit an actual form
      const finalLink = document.createElement('a');
      // open in new tab
      finalLink.target = '_blank';
      // append the user's zip code, if necessary
      finalLink.href = formatFinalZipWidgetDestinationUrl(zipWidgetEl, zipcode);
      // open the final partner page URL with zipcode appended to query string
      finalLink.click();

      // Fire New same-page leave-behind logic from _leavebehind.js
      window.dispatchEvent(new Event('leave-behind'));
    } else if (zipcode && !validators.isZipValid(zipcode)) {
      // failure.
      console.warn('zip invalid');
      zipWidgetEl.classList.add('leadgen-zipwidget--error');
    } else {
      // error.
      console.error('error parsing zip code');
    }
  });
};

/**
 * Zipwidget Obj
 * @type {{init: zipWidget.init}}
 */
const zipWidget = {
  init: () => {
    setUserZip();
    const zipWidgetElem = document.querySelectorAll('.leadgen-zipwidget__form');
    if (zipWidgetElem.length > 0) {
      console.log('ZipWidget Block Detected');
      zipWidgetElem.forEach((zipwidget) => {
        onZipWidgetSubmit(zipwidget);
      });
    }
  },
};

export default zipWidget;
